import React, { useEffect, useState } from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';

/* A stack of phones, each scrolling a full-page capture of a live site.
   Used by the Pudeuc and The Sportship case studies.
   pages: [{ file, label, path }] · images at `${imgBase}${file}.webp` · "open live" = baseUrl + path */

const useIsMobile = (bp = 768) => {
  const [mobile, setMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < bp : false);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < bp);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [bp]);
  return mobile;
};

/* One phone: a scrollable capture of a store page, a progress rail and a
   "Scroll" pill that stays until the visitor scrolls it themselves. */
const Phone = ({ page, height, imgBase, baseUrl, live = false, still = false }) => {
  const box = React.useRef(null);
  const [progress, setProgress] = useState(0);
  const [touched, setTouched] = useState(false);

  // Each time a phone comes to the front (and is on screen), it scrolls itself
  // a little way down: down only, never back up, and not at all once the
  // visitor has touched it, wheeled it or already scrolled it.
  useEffect(() => {
    const el = box.current;
    if (still || live || !el || typeof IntersectionObserver === 'undefined') return undefined;
    let handsOn = false;
    let timer;
    const stop = () => { handsOn = true; clearTimeout(timer); };
    ['wheel', 'touchstart', 'pointerdown'].forEach((t) => el.addEventListener(t, stop, { passive: true }));
    const peek = () => {
      timer = setTimeout(() => {
        if (!handsOn && el.scrollTop === 0) el.scrollTo({ top: 320, behavior: 'smooth' });
      }, 450);
    };
    const io = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      io.disconnect();
      const pic = el.querySelector('img');
      // the capture is lazy: until it has loaded there is nothing to scroll
      if (pic && !pic.complete) pic.addEventListener('load', peek, { once: true });
      else peek();
    }, { threshold: 0.5 });
    io.observe(el);
    return () => {
      io.disconnect();
      clearTimeout(timer);
      ['wheel', 'touchstart', 'pointerdown'].forEach((t) => el.removeEventListener(t, stop));
    };
  }, [still, live]);

  const onScroll = (e) => {
    const el = e.currentTarget;
    setProgress(el.scrollTop / Math.max(1, el.scrollHeight - el.clientHeight));
    if (el.scrollTop > 380) setTouched(true);
  };

  return (
    <figure style={{ margin: 0 }}>
      <div style={{
        borderRadius: '30px', padding: '10px', background: '#0d1117',
        boxShadow: '0 18px 40px rgba(0,0,0,0.25)', position: 'relative'
      }}>
        {live && !still ? (
          // the real site, running inside the phone: visitors scroll, tap and move around in it
          <iframe src={`${baseUrl}${page.path}`} title={page.label} loading="lazy"
            style={{ display: 'block', width: '100%', height, border: 0, borderRadius: '21px', background: '#fff' }} />
        ) : (
        <div ref={box} className="pd-scroll" onScroll={onScroll} style={{
          height, overflowY: 'auto', overscrollBehavior: 'contain',
          borderRadius: '21px', background: '#fff', scrollbarWidth: 'none'
        }}>
          <img src={`${imgBase}${page.file}.webp`} alt={page.label} loading="lazy"
            style={{ width: '100%', display: 'block' }} />
        </div>
        )}
        {!(live && !still) && <>
        {/* progress rail */}
        <div style={{ position: 'absolute', top: 28, bottom: 28, right: 4, width: 3, borderRadius: 3, background: 'rgba(255,255,255,0.12)' }}>
          <div style={{
            position: 'absolute', left: 0, right: 0, height: '18%', borderRadius: 3, background: 'rgba(255,255,255,0.7)',
            top: `${progress * 82}%`
          }} />
        </div>
        {/* scroll pill */}
        {!still && <motion.div
          animate={{ opacity: touched ? 0 : 1, y: touched ? 10 : [0, -6, 0] }}
          transition={touched ? { duration: 0.3 } : { duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            position: 'absolute', left: '50%', bottom: 26, marginLeft: -52, width: 104,
            padding: '8px 0', borderRadius: 999, textAlign: 'center', pointerEvents: 'none',
            background: 'rgba(13,17,23,0.82)', color: '#fff', fontSize: 11.5, letterSpacing: '0.12em',
            textTransform: 'uppercase', backdropFilter: 'blur(6px)'
          }}
        >
          Scroll ↓
        </motion.div>}
        </>}
      </div>
    </figure>
  );
};

/* The store pages as a stack of phones, the same idea as the cards further
   down: the ones behind are fanned out so it is obvious there is more. A
   sideways drag throws the top phone to the back; up and down scrolls the page
   inside it, and the two never fight (the drag only claims the x axis). */
const StorePhone = ({ page, depth, height, imgBase, baseUrl, live, onThrow }) => {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-300, 300], [-10, 10]);
  const top = depth === 0;
  const fan = [0, 5, -5, 9, -9][depth] ?? 0;
  const shift = [0, 34, -34, 60, -60][depth] ?? 0;
  return (
    <motion.div
      drag={top ? 'x' : false}
      dragSnapToOrigin
      dragElastic={0.7}
      dragDirectionLock
      onDragEnd={(e, info) => {
        if (Math.abs(info.offset.x) > 80 || Math.abs(info.velocity.x) > 500) onThrow(info.offset.x > 0 ? -1 : 1);
      }}
      style={{ x, rotate: top ? rotate : fan, position: 'absolute', inset: 0, zIndex: 10 - depth, touchAction: 'pan-y' }}
      animate={{ scale: 1 - depth * 0.04, y: depth * 8, x: top ? 0 : shift, opacity: depth > 4 ? 0 : 1 }}
      transition={{ type: 'spring', stiffness: 260, damping: 26 }}
    >
      <div style={{ pointerEvents: top ? 'auto' : 'none' }}>
        <Phone page={page} height={height} imgBase={imgBase} baseUrl={baseUrl} live={live} still={!top} />
      </div>
    </motion.div>
  );
};

const PhoneStack = ({ pages, baseUrl, imgBase, live = false }) => {
  const mobile = useIsMobile();
  const [order, setOrder] = useState(pages.map((_, i) => i));
  const step = (d) => setOrder((o) => (d > 0 ? [...o.slice(1), o[0]] : [o[o.length - 1], ...o.slice(0, -1)]));
  const jump = (i) => setOrder((o) => { const k = o.indexOf(i); return [...o.slice(k), ...o.slice(0, k)]; });
  const height = mobile ? '68vh' : 'min(780px, 82vh)';
  const page = pages[order[0]];
  const btn = {
    width: 46, height: 46, borderRadius: '50%', cursor: 'pointer', fontSize: 18,
    border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-primary)'
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '28px', padding: '16px 0 4px', overflow: 'hidden' }}>
      <style>{`.pd-scroll::-webkit-scrollbar{display:none} .pd-scroll{touch-action:pan-y;overflow-x:hidden}
        .tss-chip{position:relative;overflow:hidden;display:inline-flex;align-items:center;gap:1px;cursor:pointer;
          padding:7px 13px 7px 11px;border-radius:999px;color:#fff;font:italic 900 12.5px/1 Inter,-apple-system,sans-serif;letter-spacing:-0.03em;
          background:linear-gradient(135deg,#0b1230,#070b1f);border:1px solid rgba(124,58,237,0.55);
          box-shadow:0 0 0 1px rgba(0,212,255,0.12) inset,0 4px 14px rgba(124,58,237,0.3)}
        .tss-chip[aria-pressed="true"]{box-shadow:0 0 0 2px rgba(0,212,255,0.55),0 6px 18px rgba(124,58,237,0.45)}
        .tss-chip-plus{background:linear-gradient(135deg,#00d4ff,#7c3aed);-webkit-background-clip:text;background-clip:text;color:transparent;font-size:1.15em}
        .tss-chip-label{font:600 12px/1 Inter,-apple-system,sans-serif;letter-spacing:0;margin-left:6px;opacity:.85}
        .tss-chip::after{content:'';position:absolute;inset:0;background:linear-gradient(110deg,transparent 30%,rgba(255,255,255,0.28) 45%,transparent 60%);
          transform:translateX(-120%);animation:tss-chip-sweep 4.5s ease-in-out infinite}
        @keyframes tss-chip-sweep{0%,60%{transform:translateX(-120%)}85%,100%{transform:translateX(120%)}}
        @media (prefers-reduced-motion:reduce){.tss-chip::after{animation:none}}`}</style>
      {/* every page by name, for a quick jump; the stack stays the main way in */}
      <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '6px', maxWidth: '560px', marginBottom: '-4px' }}>
        {pages.map((pg, i) => pg.mark === 'tss' ? (
          // The Sportship's TSS+ mark, as it appears in the site's own nav: navy slab, italic TSS, gradient plus, light sweep
          <button key={pg.file} onClick={() => jump(i)} className="tss-chip" aria-pressed={i === order[0]}>
            <span className="tss-chip-word">TSS</span><span className="tss-chip-plus">+</span>
            <span className="tss-chip-label">ratings</span>
          </button>
        ) : (
          <button key={pg.file} onClick={() => jump(i)} style={{
            padding: '7px 13px', borderRadius: 999, fontSize: 12, cursor: 'pointer',
            border: '1px solid var(--border-color)',
            background: i === order[0] ? 'var(--text-primary)' : 'var(--bg-secondary)',
            color: i === order[0] ? 'var(--bg-primary)' : 'var(--text-secondary)'
          }}>{pg.label}</button>
        ))}
      </div>
      <div style={{ position: 'relative', width: mobile ? 'min(74vw, 330px)' : '370px', height: `calc(${height} + 20px)` }}>
        {order.slice(0, 5).map((idx, depth) => (
          <StorePhone key={pages[idx].file} page={pages[idx]} depth={depth} height={height} imgBase={imgBase} baseUrl={baseUrl} live={live} onThrow={step} />
        )).reverse()}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <button style={btn} onClick={() => step(-1)} aria-label="Previous page">←</button>
        <div style={{ textAlign: 'center', minWidth: 150 }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>{page.label}</div>
          <a href={`${baseUrl}${page.path}`} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>open live</a>
        </div>
        <button style={btn} onClick={() => step(1)} aria-label="Next page">→</button>
      </div>
    </div>
  );
};

export default PhoneStack;
